import type { FC } from 'react';
import React, { Fragment, useRef, useCallback, useState, useEffect } from 'react';

// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Spinner from '@atlaskit/spinner/spinner';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { ANIMATE_TIME_SEC } from '@confluence/page-tree/entry-points/PageTreeLoader.tsx';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { LoadableLazy, LoadableAfterPaint } from '@confluence/loadable';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';

import { FlexCenter } from './PresentationalComponents.tsx';
import { useSpaceViewsData } from './useSpaceViewsData.ts';
import type { SpaceViewsPersistenceOption } from './__generated__/useSpaceViewsOptionsPersistenceQuery.graphql.ts';

const SpaceViewsErrorLoader = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceViewsErrors" */ './SpaceViewsErrors.tsx'))
			.SpaceViewsError,
});

const SpaceViewsNoResultsLoader = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceViewsErrors" */ './SpaceViewsErrors.tsx'))
			.SpaceViewsNoResults,
});

const SpaceViewsCurrentViewIndicatorLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SpaceViewsCurrentViewIndicator" */ './SpaceViewsCurrentViewIndicator.tsx'
			)
		).SpaceViewsCurrentViewIndicator,
});

const ViewMoreButtonLoader = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ViewMoreButton" */ './ViewMoreButton.tsx'))
			.ViewMoreButton,
});

const SpaceViewsListLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SpaceViewsListSpaceViewsList" */ './SpaceViewsList/SpaceViewsList.tsx'
			)
		).SpaceViewsList,
});

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireItemClickedAnalytics,
);

type SpaceViewsProps = {
	spaceViewsSort: SpaceViewsPersistenceOption;
	setIsSpaceViewsLoading: (loading: boolean) => void;
	setSpaceViewsError: (error?: ApolloErrorInterop) => void;
	trackedSetSpaceViewsSort: (spaceViewsSort: SpaceViewsPersistenceOption) => void;
	makeTrackedFetchMore: (fetchMore: () => void) => () => void;
	spaceKey?: string;
	contentId?: string;
	shouldRenderAfterIcon: boolean;
	contentTreeSize?: number;
	onContentTreeLoadComplete?: () => void;
	onSpaceViewCleared?: () => void;
};

export const SpaceViews: FC<SpaceViewsProps> = ({
	spaceViewsSort,
	setIsSpaceViewsLoading,
	setSpaceViewsError,
	trackedSetSpaceViewsSort,
	makeTrackedFetchMore,
	spaceKey,
	contentId,
	shouldRenderAfterIcon,
	contentTreeSize,
	onContentTreeLoadComplete = () => {},
	onSpaceViewCleared,
	...hoverPageCardProps
}) => {
	const clearHighlightTimeout = useRef(setTimeout(() => {}, 0));
	const setClearHighlightTimeout = useCallback(
		(pageTreeStateUpdater: any) => {
			clearHighlightTimeout.current = setTimeout(() => {
				pageTreeStateUpdater.clearHighlightedPages();
			}, ANIMATE_TIME_SEC * 1000);
		},

		[clearHighlightTimeout],
	);
	const [highlightedPages, setHighlightedPages] = useState<string[]>([]);

	const { data, loading, error, fetchMore, refetch } = useSpaceViewsData(spaceViewsSort);
	const pagesList = data?.pagesList;

	useEffect(() => {
		setIsSpaceViewsLoading(loading);
		if (!loading) {
			onContentTreeLoadComplete();
		}
	}, [setIsSpaceViewsLoading, loading, onContentTreeLoadComplete]);

	useEffect(() => {
		setSpaceViewsError(error);
	}, [setSpaceViewsError, error]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onItemClick = useLazyClickAnalytics(createAnalyticsEvent, spaceViewsSort, contentTreeSize);

	return (
		<Fragment>
			<SpaceViewsCurrentViewIndicatorLoader
				spaceViewsSort={spaceViewsSort}
				setSpaceViewsSort={trackedSetSpaceViewsSort}
				onSpaceViewCleared={onSpaceViewCleared}
			/>
			{/**we remove the space root so this is the proper empty check**/}
			{pagesList?.length > 0 ? (
				<SpaceViewsListLoader
					pagesList={pagesList}
					spaceKey={spaceKey}
					contentId={contentId}
					onItemClick={onItemClick}
					shouldRenderAfterIcon={shouldRenderAfterIcon}
					{...hoverPageCardProps}
				/>
			) : (
				!error && !loading && <SpaceViewsNoResultsLoader />
			)}
			{loading ? (
				<FlexCenter>
					<Spinner size="medium" />
				</FlexCenter>
			) : error ? (
				<SpaceViewsErrorLoader error={error} />
			) : (
				<ViewMoreButtonLoader
					fetchMore={makeTrackedFetchMore(fetchMore)}
					hasMore={Boolean(data?.pageInfo?.hasNextPage)}
					loading={loading}
				/>
			)}
			<Subscribe to={[PageTreeStateUpdater]}>
				{(pageTreeStateUpdater: PageTreeStateUpdater) => {
					if (
						refetch &&
						(pageTreeStateUpdater.state.forceFetchPageId ||
							pageTreeStateUpdater.state.highlightPagesIds.length !== highlightedPages.length)
					) {
						if (pageTreeStateUpdater.state.highlightPagesIds.length !== highlightedPages.length) {
							setHighlightedPages(pageTreeStateUpdater.state.highlightPagesIds);
							if (pageTreeStateUpdater.state.highlightPagesIds.length > 0) {
								clearHighlightTimeout.current && clearTimeout(clearHighlightTimeout.current);
								setClearHighlightTimeout(pageTreeStateUpdater);
							}
						}
						void refetch();
						pageTreeStateUpdater.clearForceFetch();
					}
					return null;
				}}
			</Subscribe>
		</Fragment>
	);
};
